import { useEffect, useMemo, useState } from 'react'
import type { Account } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { SearchableSelect, type SelectOption } from './SearchableSelect'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { signPrefix } from '../lib/amount-format'
import {
  ACCOUNT_TYPE_KEYS,
  accountRole,
  transferPattern,
  transferPatternKeys,
  transferTargets,
  type AccountRole,
} from '../lib/transfer'

/** 出金元の情報。一括編集のように口座が確定しない場合もあるため口座と役割を分けて持つ */
export interface TransferSource {
  /** 出金元口座。複数の取引にまたがり口座が一意に決まらないときは null */
  account: Account | null
  /** 出金元の役割。account が null でも種別が揃っていれば絞り込みに使える */
  role: AccountRole | null
  /** 方向図の出金元側に出す名前 */
  name: string
  /** 名前の下に出す補足（種別名など）。無ければ null */
  caption: string | null
  /** 出金元が複数の取引にまたがっていて一意に決まらない（一括編集）ことを示す */
  mixed?: boolean
}

interface TransferFieldsProps {
  accounts: Account[]
  source: TransferSource
  /** 選択中の振替先口座ID。未選択は空文字 */
  value: string
  onChange: (toAccountId: string) => void
  required: boolean
  /** 未選択時の option ラベル */
  placeholder: string
  /** 振替先の候補から外す口座ID（出金元自身など） */
  excludeIds?: string[]
  /** 方向図に増減を出すための金額。未入力・不明なら undefined */
  amount?: number
  currency?: string
}

function fill(template: string, params: Record<string, string>): string {
  return Object.entries(params).reduce((acc, [key, val]) => acc.replaceAll(`{${key}}`, val), template)
}

/**
 * 振替の「出金元 → 振替先」を、向き・候補の絞り込み・意味の説明つきで入力させる。
 *
 * 振替先は出金元の種別から見て自然な候補（銀行口座ならクレジットカードの引き落とし、
 * クレジットカードなら銀行口座への返金 など）を用途ごとに分けて並べ、
 * それ以外の口座は「すべての口座から選ぶ」で解除するまで出さない。
 */
export function TransferFields({
  accounts,
  source,
  value,
  onChange,
  required,
  placeholder,
  excludeIds = [],
  amount,
  currency = 'JPY',
}: TransferFieldsProps) {
  const { t } = useLanguage()
  const { formatCurrency } = useAmountFormat()
  const [showAll, setShowAll] = useState(false)

  const excludeKey = excludeIds.join(',')
  const targets = useMemo(
    () => transferTargets(accounts, source.role, excludeKey ? excludeKey.split(',') : []),
    [accounts, source.role, excludeKey],
  )

  // 既存の振替先がおすすめ外（連携由来や種別変更後など）なら、消えないように全件表示に切り替える
  useEffect(() => {
    if (value && targets.others.some((a) => a.id === value)) setShowAll(true)
  }, [value, targets])

  const hasRecommended = targets.recommended.length > 0
  const visibleOthers = showAll || !hasRecommended ? targets.others : []
  const noOption = !hasRecommended && targets.others.length === 0

  const toAccount = accounts.find((a) => a.id === value) ?? null
  const pattern =
    source.role && toAccount ? transferPattern(source.role, accountRole(toAccount.type)) : null
  const patternKeys = pattern ? transferPatternKeys(pattern) : null
  const isUnusual = pattern === 'unusual'

  const formatAmount = (delta: number) => {
    // 0 のときは符号を付けない（増減が無いのに ± が付いて見える）
    const sign = signPrefix(delta)
    return `${sign === '-' ? '−' : sign}${formatCurrency(Math.abs(delta), currency)}`
  }

  const sourceTypeLabel = source.caption
  const toTypeLabel = toAccount ? t(ACCOUNT_TYPE_KEYS[toAccount.type]) : null

  const optionLabel = (a: Account) => `${a.name}（${t(ACCOUNT_TYPE_KEYS[a.type])}）`

  // おすすめは「カードの引き落とし」など用途ごとの見出しでまとめ、それ以外は「その他」に寄せる
  const options: SelectOption[] = [
    { value: '', label: noOption ? t('transactions_form_to_account_no_option') : placeholder },
    ...targets.recommended.flatMap((group) =>
      group.accounts.map((a) => ({
        value: a.id,
        label: optionLabel(a),
        group: t(transferPatternKeys(group.pattern).label),
      })),
    ),
    ...visibleOthers.map((a) => ({
      value: a.id,
      label: optionLabel(a),
      group: hasRecommended ? t('transactions_form_to_account_group_others') : undefined,
    })),
  ]

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
        {t('transactions_form_to_account')} {required && <span className="text-red-500">*</span>}
      </label>
      <SearchableSelect
        options={options}
        value={value}
        onChange={onChange}
        placeholder={noOption ? t('transactions_form_to_account_no_option') : placeholder}
        searchPlaceholder={t('transactions_filter_search_accounts')}
        disabled={noOption}
        className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
      />

      {hasRecommended && targets.others.length > 0 && (
        <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900"
          />
          {t('transactions_form_to_account_show_all')}
        </label>
      )}

      {/* 向きと意味の可視化: どちらからどちらへ、いくら動き、それが何を表すのか */}
      <div
        className={`mt-2 rounded-lg border ${
          isUnusual
            ? 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40'
            : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50'
        }`}
      >
        <div className="flex items-stretch gap-2 p-3">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t('transactions_transfer_from_label')}
            </p>
            <p className="mt-0.5 text-sm font-medium text-gray-900 truncate dark:text-gray-100">{source.name}</p>
            {sourceTypeLabel && (
              <p className="text-xs text-gray-500 truncate dark:text-gray-400">{sourceTypeLabel}</p>
            )}
            {amount !== undefined && amount > 0 && (
              <p className="mt-1 text-xs font-medium text-red-600 dark:text-red-400">{formatAmount(-amount)}</p>
            )}
          </div>
          <div className="flex items-center px-1 text-gray-400 dark:text-gray-500" aria-hidden="true">
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M3 10a1 1 0 011-1h9.586l-3.293-3.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 11H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t('transactions_transfer_to_label')}
            </p>
            <p
              className={`mt-0.5 text-sm font-medium truncate ${
                toAccount ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              {toAccount ? toAccount.name : t('transactions_transfer_unselected')}
            </p>
            {toTypeLabel && <p className="text-xs text-gray-500 truncate dark:text-gray-400">{toTypeLabel}</p>}
            {toAccount && amount !== undefined && amount > 0 && (
              <p className="mt-1 text-xs font-medium text-green-600 dark:text-green-400">{formatAmount(amount)}</p>
            )}
          </div>
        </div>
        <div
          className={`border-t px-3 py-2 text-xs ${
            isUnusual
              ? 'border-amber-300 text-amber-800 dark:border-amber-800 dark:text-amber-300'
              : 'border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-400'
          }`}
        >
          {patternKeys && toAccount ? (
            <>
              <p className="font-medium">{t(patternKeys.label)}</p>
              <p className="mt-0.5">
                {fill(t(patternKeys.desc), {
                  from: source.name,
                  to: toAccount.name,
                  fromType: sourceTypeLabel ?? '',
                  toType: toTypeLabel ?? '',
                })}
              </p>
              {!isUnusual && <p className="mt-0.5">{t('transactions_transfer_excluded_note')}</p>}
            </>
          ) : (
            <p>
              {source.role && sourceTypeLabel
                ? fill(t('transactions_transfer_from_hint'), { type: sourceTypeLabel })
                : source.mixed
                ? t('transactions_transfer_mixed_hint')
                : t('transactions_transfer_from_unknown_hint')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
